import type { BetLinesPort } from "@slot/game-shell";
import type { GridLayer, TweenEasing } from "@slot/presentation";
import { BASE_CELL_COUNT, GRID_SIZE, cellIndex } from "../../gridSize";
import { lineRows } from "../../rules/lines";
import { BLANK } from "../../rules/symbols";
import type { GridContainer } from "../../scene/GridContainer";
import { BLANK_LIT } from "../../symbols/SymbolCatalog";
import { LINES_TUNING } from "../../tuning/featureTuning";

/**
 * The active lines on the board (decision 56), after 3 Coin Volcanoes': in the base game the rows the bet's lines cover
 * are lit — every cell on the red background, an empty one with the red badge, rolling or still — and the row frame goes
 * around them; a change of the lines lights the new rows at once, the frame sliding and stretching to them (eased). The
 * bonus plays no line: its board is unlit and unframed. The codec (`CoinGridCodec`) and the rolling cells' fillers ask
 * `isLit` for a cell.
 */
export class ActiveLinesPresenter {
  private inBonus = false;
  private lit: ReadonlySet<number> = new Set();
  /** The rows lit last, so a change of the lines is told apart from the bet's other news. */
  private litKey = "";

  public constructor(
    private readonly input: {
      grid: GridContainer;
      gridLayer: GridLayer;
      betLines: BetLinesPort;
      /** The lines changed (the rows lit are others): the row frame's glint. */
      onLinesChanged?: () => void;
    },
  ) {
    input.betLines.subscribe(() => this.apply(LINES_TUNING.frameResize));
    this.apply();
  }

  /** Whether `index` is an active line's cell now. */
  public isLit(index: number): boolean {
    return this.lit.has(index);
  }

  /** The rows lit now, top to bottom (the dev hook's read): none in the bonus. */
  public get litRows(): readonly number[] {
    return this.inBonus ? [] : lineRows(this.input.betLines.getSnapshot().lines);
  }

  /** The bonus starts or ends. */
  public setBonus(active: boolean): void {
    this.inBonus = active;
    this.apply();
  }

  /** Lights the rows the lines cover now; the frame moves to them with `frameMotion` (a change of the lines), else at once. */
  private apply(frameMotion?: { seconds: number; easing: TweenEasing }): void {
    const rows = this.litRows;
    const key = rows.join(",");
    const changed = key !== this.litKey;
    this.litKey = key;
    this.lit = new Set(rows.flatMap((row) => Array.from({ length: GRID_SIZE.columns }, (_, col) => cellIndex(row, col))));
    this.input.grid.setLit(this.lit);
    this.input.grid.setRowFrame(rows.length > 0 ? rows : null, frameMotion);
    if (changed && frameMotion && rows.length > 0) this.input.onLinesChanged?.();
    // The board's empty cells follow: a cell rolling now takes its look from the frame it lands on.
    const layer = this.input.gridLayer;
    for (let index = 0; index < BASE_CELL_COUNT; index++) {
      const view = layer.getSymbolViewAtIndex(index);
      const id = view?.getSymbolId();
      if (!view || (id !== BLANK && id !== BLANK_LIT)) continue;
      const wanted = this.lit.has(index) ? BLANK_LIT : BLANK;
      if (id !== wanted) layer.pool.applySymbolIdToView(view, wanted);
    }
  }
}
